import React, { useMemo, useRef, useState } from 'react';
import { Button, Divider, Input, Select, message } from 'antd';
import { PlusOutlined, SearchOutlined, TagOutlined } from '@ant-design/icons';
import { callCenterSubjectsApi } from '../../../utils/api';
import { usePermissions } from '../../../contexts/PermissionsContext';
import CallSubjectChip from './CallSubjectChip';
import { nextSubjectColor } from './subjectColors';
import { refreshCallSubjects, useCallSubjects } from './useCallSubjects';

/** یکسان‌سازی حروف عربی/فارسی و فاصله‌ها برای جستجو */
const normalize = (value) => String(value ?? '')
  .replace(/ي/g, 'ی')
  .replace(/ك/g, 'ک')
  .replace(/[‌\s]+/g, ' ')
  .trim()
  .toLowerCase();

/**
 * انتخاب موضوع تماس با چیپ رنگی، جستجوی سریع (نام و توضیحات) و ساخت موضوع جدید از همان عبارت جستجو.
 *
 * @param {number|null} value شناسه موضوع
 * @param {(id: number|null, subject: object|null) => void} onChange
 * @param {boolean} borderless نمایش فشرده برای سلول‌های جدول
 */
const CallSubjectSelect = ({
  value,
  onChange,
  loading = false,
  borderless = false,
  placeholder = 'انتخاب موضوع',
  className = '',
  style,
}) => {
  const { subjects, loading: subjectsLoading } = useCallSubjects();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_callcenter_subjects');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const searchRef = useRef(null);

  const byId = useMemo(() => new Map(subjects.map((s) => [s.id, s])), [subjects]);

  const filtered = useMemo(() => {
    const q = normalize(search);
    if (!q) return subjects;
    return subjects.filter((s) => normalize(s.name).includes(q) || normalize(s.description).includes(q));
  }, [subjects, search]);

  const exactMatch = useMemo(
    () => subjects.some((s) => normalize(s.name) === normalize(search)),
    [subjects, search],
  );

  const options = useMemo(() => filtered.map((subject) => ({
    value: subject.id,
    label: subject.name,
    subject,
  })), [filtered]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) {
      // فوکوس مستقیم روی جستجو تا کاربر بلافاصله تایپ کند
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  };

  const select = (subject) => {
    onChange?.(subject?.id ?? null, subject ?? null);
    handleOpenChange(false);
  };

  const createFromSearch = async () => {
    const name = search.trim();
    if (!name || exactMatch) return;
    setCreating(true);
    try {
      const res = await callCenterSubjectsApi.create({ name, color: nextSubjectColor(subjects) });
      await refreshCallSubjects();
      message.success(`موضوع «${name}» ساخته شد`);
      if (res?.data?.id) select(res.data);
    } catch (error) {
      message.error(error?.message || 'خطا در ساخت موضوع');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Select
      value={value ?? undefined}
      onChange={(id) => select(id ? byId.get(id) ?? null : null)}
      open={open}
      onOpenChange={handleOpenChange}
      options={options}
      loading={loading || subjectsLoading}
      disabled={loading}
      allowClear
      showSearch={false}
      placeholder={(
        <span className="custom-font text-xs flex items-center gap-1">
          <TagOutlined /> {placeholder}
        </span>
      )}
      variant={borderless ? 'borderless' : 'outlined'}
      className={`custom-font ${className}`}
      style={{ minWidth: 150, ...style }}
      classNames={{ popup: { root: 'custom-font' } }}
      popupMatchSelectWidth={260}
      labelRender={({ value: selected }) => {
        const subject = byId.get(selected);
        return subject ? <CallSubjectChip subject={subject} size="small" /> : null;
      }}
      optionRender={(option) => (
        <div className="flex flex-col gap-0.5 py-0.5">
          <CallSubjectChip subject={{ ...option.data.subject, description: undefined }} size="small" className="self-start" />
          {option.data.subject.description && (
            <span className="text-[11px] text-gray-400 truncate">{option.data.subject.description}</span>
          )}
        </div>
      )}
      notFoundContent={(
        <span className="custom-font text-xs text-gray-400 block py-2 text-center">
          {search ? 'موضوعی با این عبارت پیدا نشد.' : 'موضوعی تعریف نشده است.'}
        </span>
      )}
      popupRender={(menu) => (
        <>
          <div className="p-2 pb-1.5">
            <Input
              ref={searchRef}
              size="small"
              allowClear
              value={search}
              prefix={<SearchOutlined className="text-gray-400" />}
              placeholder="جستجوی موضوع..."
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (filtered.length > 0) select(filtered[0]);
                  else if (canManage) createFromSearch();
                }
                if (e.key === 'Escape') handleOpenChange(false);
              }}
              className="custom-font"
            />
          </div>
          {menu}
          {canManage && search.trim() && !exactMatch && (
            <>
              <Divider className="my-1" />
              <Button
                type="text"
                size="small"
                block
                icon={<PlusOutlined />}
                loading={creating}
                onClick={createFromSearch}
                className="custom-font text-xs text-teal-700 dark:text-teal-300 justify-start mb-1"
              >
                افزودن «{search.trim()}» به عنوان موضوع جدید
              </Button>
            </>
          )}
        </>
      )}
    />
  );
};

export default CallSubjectSelect;
